import { z } from 'zod'

/**
 * Configuration service that provides type-safe access to environment variables.
 * All environment variables should be accessed through this service.
 */

// Schema for runtime environment variables
const envSchema = z.object({
    // Supabase
    VITE_SUPABASE_URL: z.string().url(),
    VITE_SUPABASE_ANON_KEY: z.string().min(1),
    VITE_WORKERS_URL: z.string().url().optional(),

    // Feature flags
    VITE_ENABLE_ANALYTICS: z.coerce.boolean().optional().default(false),

    // Environment
    NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),

    // Base URL
    BASE_URL: z.string().optional().default('/'),

    // Product name - can be overridden by environment variable
    VITE_PRODUCT_NAME: z.string().optional().default('Robo Relay')
})

// Type inference
type Env = z.infer<typeof envSchema>

// Parse and validate environment variables
function validateEnv(): Env {
    const processEnv = {
        VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
        VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
        VITE_WORKERS_URL: import.meta.env.VITE_WORKERS_URL,
        VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
        NODE_ENV: import.meta.env.MODE, // Vite provides this as MODE
        BASE_URL: import.meta.env.BASE_URL,
        VITE_PRODUCT_NAME: import.meta.env.VITE_PRODUCT_NAME
    }

    try {
        return envSchema.parse(processEnv)
    } catch (error) {
        if (error instanceof z.ZodError) {
            const missingVars = error.issues.map(issue => issue.path.join('.'))
            throw new Error(`Missing or invalid environment variables: ${missingVars.join(', ')}. Available vars: ${JSON.stringify(processEnv)}`)
        }
        throw error
    }
}

// Singleton instance of validated environment variables
export const env = validateEnv()

// Type-safe config getter
export function getConfig<T extends keyof Env>(key: T): Env[T] {
    return env[key]
}

// Helper to check if we're in development mode
export const isDevelopment = env.NODE_ENV === 'development'

// Helper to check if we're in production mode
export const isProduction = env.NODE_ENV === 'production'

// Helper to check if we're in test mode
export const isTest = env.NODE_ENV === 'test'

export const isPreview = env.NODE_ENV === 'development' || env.NODE_ENV === 'test'

// Export commonly used config values
export const productName = env.VITE_PRODUCT_NAME 